import "./merge-pdfs.css";
import { pdfjs } from "./pdfjs-adapter.js";
import { fileListToArray } from "./file-list-core.js";
import {
  hasPdfHeader,
  moveMergeItem,
  normalizeMergedName,
  validateMergeCollection,
  validateMergeFile,
  validateMergedPageCount
} from "./merge-pdf-core.js";
import { mergePdfDocuments } from "./merge-pdf-export.js";
import { createOperationResult } from "./operation-result.js";

const root = document.querySelector("[data-merge-pdfs]");

if (root) {
  const ids = [
    "mergeStatus", "mergeUploadPanel", "mergeDropZone", "choosePdfsButton", "mergeFileInput",
    "mergeWorkspace", "mergeCount", "mergePageCount", "addMorePdfs", "clearPdfs", "mergeButton",
    "mergeProgress", "mergeList", "mergeOutputName"
  ];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const state = { items: [], nextId: 1, busy: false, draggedId: null };
  const operationResult = createOperationResult(root, elements.mergeWorkspace);

  function showStatus(message) {
    elements.mergeStatus.textContent = message;
    elements.mergeStatus.hidden = !message;
  }

  function clearStatus() {
    elements.mergeStatus.textContent = "";
    elements.mergeStatus.hidden = true;
  }

  function openWorkspace() {
    elements.mergeUploadPanel.hidden = true;
    elements.mergeWorkspace.hidden = false;
    root.classList.add("is-arranging");
    document.body.classList.add("merge-active");
  }

  function closeWorkspace() {
    elements.mergeWorkspace.hidden = true;
    elements.mergeUploadPanel.hidden = false;
    root.classList.remove("is-arranging");
    document.body.classList.remove("merge-active");
  }

  async function addFiles(fileList) {
    const files = fileListToArray(fileList);
    if (!files.length || state.busy) return;
    clearStatus();
    const pending = [];
    const rejected = [];

    for (const file of files) {
      const fileError = validateMergeFile(file);
      const collectionError = validateMergeCollection(state.items, [{ size: file.size }]);
      if (fileError || collectionError) {
        rejected.push(`${file.name}: ${fileError || collectionError}`);
        continue;
      }
      const item = {
        id: `pdf-${state.nextId++}`,
        name: file.name,
        size: file.size,
        file,
        bytes: null,
        pageCount: 0,
        thumbnailUrl: null,
        status: "pending"
      };
      state.items.push(item);
      pending.push(item);
    }

    if (state.items.length) openWorkspace();
    renderItems();
    elements.mergeFileInput.value = "";
    await new Promise(requestAnimationFrame);

    await processWithConcurrency(pending, 2, async item => {
      try {
        const bytes = new Uint8Array(await item.file.arrayBuffer());
        if (!hasPdfHeader(bytes)) throw new Error("File content is not a valid PDF.");
        const document = await pdfjs.getDocument({ data: bytes.slice(), password: "" }).promise;
        try {
          if (!state.items.includes(item)) return;
          const page = await document.getPage(1);
          const thumbnailUrl = await renderThumbnail(page);
          if (!state.items.includes(item)) {
            URL.revokeObjectURL(thumbnailUrl);
            return;
          }
          const currentPages = state.items
            .filter(value => value !== item && value.status === "ready")
            .reduce((sum, value) => sum + value.pageCount, 0);
          const pageError = validateMergedPageCount(currentPages, document.numPages);
          if (pageError) {
            URL.revokeObjectURL(thumbnailUrl);
            throw new Error(pageError);
          }
          item.bytes = bytes;
          item.pageCount = document.numPages;
          item.thumbnailUrl = thumbnailUrl;
          item.status = "ready";
        } finally {
          await document.destroy();
        }
      } catch (error) {
        if (!state.items.includes(item)) return;
        removeItem(item, false);
        const message = error?.name === "PasswordException"
          ? "Password-protected PDFs are not supported."
          : error?.message || "Could not read this PDF.";
        rejected.push(`${item.name}: ${message}`);
      } finally {
        renderItems();
      }
    });

    renderItems();
    if (rejected.length) showStatus(`Some PDFs were skipped. ${rejected.join(" ")}`);
  }

  async function processWithConcurrency(items, concurrency, task) {
    let cursor = 0;
    const worker = async () => {
      while (cursor < items.length) await task(items[cursor++]);
    };
    await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker));
  }

  async function renderThumbnail(page) {
    const natural = page.getViewport({ scale: 1 });
    const scale = Math.min(220 / natural.width, 160 / natural.height, 1);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.ceil(viewport.width));
    canvas.height = Math.max(1, Math.ceil(viewport.height));
    await page.render({ canvasContext: canvas.getContext("2d", { alpha: false }), viewport }).promise;
    const blob = await new Promise((resolve, reject) => canvas.toBlob(
      value => value ? resolve(value) : reject(new Error("Could not create a PDF preview.")),
      "image/png"
    ));
    canvas.width = 1;
    canvas.height = 1;
    return URL.createObjectURL(blob);
  }

  function renderItems() {
    elements.mergeList.replaceChildren();
    state.items.forEach((item, index) => elements.mergeList.append(createCard(item, index)));
    elements.mergeCount.textContent = String(state.items.length);
    elements.mergePageCount.textContent = String(state.items.reduce((sum, item) => sum + item.pageCount, 0));
    updateControls();
    if (!state.items.length) closeWorkspace();
  }

  function updateControls() {
    const pending = state.items.filter(item => item.status === "pending").length;
    const ready = state.items.filter(item => item.status === "ready").length;
    elements.mergeButton.disabled = state.busy || pending > 0 || ready < 2 || ready !== state.items.length;
    elements.addMorePdfs.disabled = state.busy;
    elements.clearPdfs.disabled = state.busy;
    if (state.busy) return;
    if (pending) {
      elements.mergeProgress.hidden = false;
      elements.mergeProgress.textContent = `Ready ${ready} of ${state.items.length}`;
    } else if (ready === 1) {
      elements.mergeProgress.hidden = false;
      elements.mergeProgress.textContent = "Add at least one more PDF";
    } else {
      elements.mergeProgress.hidden = true;
    }
  }

  function createCard(item, index) {
    const card = document.createElement("article");
    card.className = `merge-card${item.status === "pending" ? " is-pending" : ""}`;
    card.dataset.id = item.id;
    card.draggable = !state.busy;
    card.setAttribute("aria-label", `Document ${index + 1}: ${item.name}`);

    const preview = document.createElement("div");
    preview.className = "merge-preview";
    if (item.thumbnailUrl) {
      const image = document.createElement("img");
      image.src = item.thumbnailUrl;
      image.alt = "";
      preview.append(image);
    } else {
      const label = document.createElement("span");
      label.textContent = "Reading PDF…";
      preview.append(label);
    }

    const badge = document.createElement("span");
    badge.className = "document-badge";
    badge.textContent = String(index + 1);

    const details = document.createElement("div");
    details.className = "merge-details";
    const name = document.createElement("strong");
    name.textContent = item.name;
    name.title = item.name;
    const meta = document.createElement("span");
    meta.textContent = item.status === "pending"
      ? `${formatBytes(item.size)} · Inspecting…`
      : `${formatBytes(item.size)} · ${item.pageCount} ${item.pageCount === 1 ? "page" : "pages"}`;
    details.append(name, meta);

    const actions = document.createElement("div");
    actions.className = "merge-card-actions";
    actions.append(
      actionButton("←", "Move document earlier", "previous", state.busy || index === 0),
      actionButton("→", "Move document later", "next", state.busy || index === state.items.length - 1),
      actionButton("×", "Remove PDF", "remove", state.busy)
    );
    actions.addEventListener("click", event => handleCardAction(event, item.id));

    card.addEventListener("dragstart", event => {
      if (state.busy) { event.preventDefault(); return; }
      state.draggedId = item.id;
      card.classList.add("is-dragging");
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", item.id);
    });
    card.addEventListener("dragend", clearDragState);
    card.addEventListener("dragover", event => { if (!state.busy) { event.preventDefault(); card.classList.add("is-drop-target"); } });
    card.addEventListener("dragleave", () => card.classList.remove("is-drop-target"));
    card.addEventListener("drop", event => {
      event.preventDefault();
      if (!state.busy) reorderById(state.draggedId || event.dataTransfer.getData("text/plain"), item.id);
    });
    card.append(preview, badge, details, actions);
    return card;
  }

  function actionButton(text, label, action, disabled) {
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = text;
    button.title = label;
    button.setAttribute("aria-label", label);
    button.dataset.action = action;
    button.disabled = disabled;
    return button;
  }

  function handleCardAction(event, id) {
    if (state.busy) return;
    const action = event.target.closest("button")?.dataset.action;
    const index = state.items.findIndex(item => item.id === id);
    if (!action || index < 0) return;
    if (action === "previous") state.items = moveMergeItem(state.items, index, index - 1);
    if (action === "next") state.items = moveMergeItem(state.items, index, index + 1);
    if (action === "remove") removeItem(state.items[index], false);
    renderItems();
  }

  function reorderById(sourceId, targetId) {
    const from = state.items.findIndex(item => item.id === sourceId);
    const to = state.items.findIndex(item => item.id === targetId);
    state.items = moveMergeItem(state.items, from, to);
    clearDragState();
    renderItems();
  }

  function clearDragState() {
    state.draggedId = null;
    document.querySelectorAll(".merge-card").forEach(card => card.classList.remove("is-dragging", "is-drop-target"));
  }

  function removeItem(item, shouldRender = true) {
    if (item.thumbnailUrl) URL.revokeObjectURL(item.thumbnailUrl);
    const index = state.items.indexOf(item);
    if (index >= 0) state.items.splice(index, 1);
    if (shouldRender) renderItems();
  }

  function formatBytes(bytes) {
    return bytes < 1024 * 1024
      ? `${Math.max(1, Math.round(bytes / 1024))} KB`
      : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  async function downloadMergedPdf() {
    const ready = state.items.filter(item => item.status === "ready");
    if (state.busy || ready.length < 2 || ready.length !== state.items.length) return;
    clearStatus();
    state.busy = true;
    elements.mergeButton.textContent = "Merging…";
    elements.mergeProgress.hidden = false;
    renderItems();
    try {
      const bytes = await mergePdfDocuments(ready, (current, total) => {
        elements.mergeProgress.textContent = `Merging page ${current} of ${total}`;
      });
      const pageCount = ready.reduce((sum, item) => sum + item.pageCount, 0);
      operationResult.show({
        blob: new Blob([bytes], { type: "application/pdf" }),
        fileName: normalizeMergedName(elements.mergeOutputName.value),
        message: "Your PDFs were merged successfully.",
        details: [`${ready.length} PDFs · ${pageCount} ${pageCount === 1 ? "page" : "pages"}`]
      });
    } catch (error) {
      showStatus(error?.message || "smallpdf.app could not merge these PDFs.");
    } finally {
      state.busy = false;
      elements.mergeButton.textContent = "Merge PDFs";
      elements.mergeProgress.hidden = true;
      renderItems();
    }
  }

  function clearAll() {
    if (state.items.length && !window.confirm("Remove all PDFs and start over?")) return;
    state.items.forEach(item => { if (item.thumbnailUrl) URL.revokeObjectURL(item.thumbnailUrl); });
    state.items = [];
    clearStatus();
    renderItems();
  }

  elements.choosePdfsButton.addEventListener("click", event => { event.stopPropagation(); elements.mergeFileInput.click(); });
  elements.addMorePdfs.addEventListener("click", () => elements.mergeFileInput.click());
  elements.mergeFileInput.addEventListener("change", () => addFiles(elements.mergeFileInput.files));
  elements.mergeDropZone.addEventListener("click", event => { if (event.target !== elements.choosePdfsButton) elements.mergeFileInput.click(); });
  elements.mergeDropZone.addEventListener("keydown", event => {
    if (event.key === "Enter" || event.key === " ") { event.preventDefault(); elements.mergeFileInput.click(); }
  });
  for (const name of ["dragenter", "dragover"]) elements.mergeDropZone.addEventListener(name, event => { event.preventDefault(); elements.mergeDropZone.classList.add("is-dragging"); });
  for (const name of ["dragleave", "drop"]) elements.mergeDropZone.addEventListener(name, event => { event.preventDefault(); elements.mergeDropZone.classList.remove("is-dragging"); });
  elements.mergeDropZone.addEventListener("drop", event => addFiles(event.dataTransfer.files));
  elements.clearPdfs.addEventListener("click", clearAll);
  elements.mergeButton.addEventListener("click", downloadMergedPdf);
  document.querySelectorAll("[data-merge-back]").forEach(link => link.addEventListener("click", event => {
    if (state.items.length && !window.confirm("Leave this tool and discard the arranged PDFs?")) event.preventDefault();
  }));
  window.addEventListener("beforeunload", event => {
    if (!state.items.length || operationResult.isVisible) return;
    event.preventDefault();
    event.returnValue = "";
  });
  window.addEventListener("pagehide", () => { operationResult.dispose(); state.items.forEach(item => { if (item.thumbnailUrl) URL.revokeObjectURL(item.thumbnailUrl); }); });
}
